/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import Code from '@atlaskit/code/code';
import { cssMap, jsx } from '@atlaskit/css';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Table } from '@atlaskit/table/primitives/table';
import { TBody } from '@atlaskit/table/primitives/tbody';
import { TH } from '@atlaskit/table/primitives/th';
import { THead } from '@atlaskit/table/primitives/thead';
import { TR } from '@atlaskit/table/primitives/tr';
import { Cell as TD } from '@atlaskit/table/td';

import HorizontalScrollContainer from '../../horizontal-scroll-container';

const styles = cssMap({
	container: {
		width: '0px', // This is a hack to force the table to be as wide as the container
		'@media (min-width: 48rem)': {
			width: '100%',
		},
	},
});

type TableValue = {
	component: string;
	exitTokens: string[];
	entryTokens: string[];
	difference: string;
};

const values: TableValue[] = [
	{
		component: 'Dropdown',
		entryTokens: ['motion.duration.short', 'motion.easing.out.bold'],
		exitTokens: ['motion.duration.xshort', 'motion.easing.in.practical'],
		difference: 'Entrance orients the user to options, exit follows their selection',
	},
	{
		component: 'Flag',
		entryTokens: ['motion.duration.long', 'motion.easing.out.bold'],
		exitTokens: ['motion.duration.medium', 'motion.easing.in.practical'],
		difference: 'Needs to appear quickly and dismiss without lingering',
	},
	{
		component: 'Modal',
		entryTokens: ['motion.duration.long', 'motion.easing.inout.bold'],
		exitTokens: ['motion.duration.medium', 'motion.easing.in.practical'],
		difference: 'Entrance establishes context, exit is a user-initiated dismissal',
	},
];

/**
 * __Exit implied table__
 */
export const MotionExitImpliedTable = (): React.JSX.Element => {
	return (
		<HorizontalScrollContainer>
			<div css={styles.container}>
				<Table testId="exit-implied-table">
					<THead>
						<TR isBodyRow={false}>
							<TH width="100px">Component</TH>
							<TH width="200px">Exit tokens</TH>
							<TH width="200px">Entry tokens</TH>
							<TH width="400px">Difference</TH>
						</TR>
					</THead>
					<TBody>
						{values.map((token) => {
							return (
								<TR key={token.component}>
									<TD>
										<Text>{token.component}</Text>
									</TD>
									<TD>
										<Stack space="space.050">
											{token.entryTokens.map((token) => (
												<Text key={token}>
													<Code>{token}</Code>
												</Text>
											))}
										</Stack>
									</TD>
									<TD>
										<Stack space="space.050">
											{token.exitTokens.map((token) => (
												<Text key={token}>
													<Code>{token}</Code>
												</Text>
											))}
										</Stack>
									</TD>
									<TD>
										<Text>{token.difference}</Text>
									</TD>
								</TR>
							);
						})}
					</TBody>
				</Table>
			</div>
		</HorizontalScrollContainer>
	);
};
